import * as Sentry from "@sentry/vue";
import { watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useUsersStore } from "@/stores/users";

/**
 * Composable for managing Sentry user context and performance monitoring
 */
export function useSentry() {
  const authStore = useAuthStore();
  const usersStore = useUsersStore();

  // Watch for authentication and user data changes to update user context
  watch(
    () => ({ authUser: authStore.user, appUser: usersStore.user }),
    ({ authUser, appUser }) => {
      if (authUser) {
        // Use the app user's display name if available, fallback to auth user data
        const displayName = appUser?.username || authUser.username;

        // Set user context when authenticated
        Sentry.setUser({
          id: authUser.id, // Keep auth0 ID as primary identifier
          email: authUser.email,
          username: displayName, // Use the proper display name
        });

        // Add user tags for better filtering
        Sentry.setTag("user.authenticated", true);
        Sentry.setTag("user.has_userId", !!authUser.userId);
        Sentry.setTag("user.auth0_id", authUser.id); // Add auth0 ID as tag for reference
        if (authUser.email) {
          Sentry.setTag("user.domain", authUser.email.split("@")[1]);
        }

        // Add additional context with both user identifiers
        Sentry.setContext("user_details", {
          auth0_id: authUser.id,
          app_user_id: appUser?.id,
          display_name: displayName,
          auth_username: authUser.username,
        });
      } else {
        // Clear user context when not authenticated
        Sentry.setUser(null);
        Sentry.setTag("user.authenticated", false);
      }
    },
    { immediate: true }
  );

  /**
   * Set additional context for the current session
   */
  const setContext = (key: string, context: Record<string, any>) => {
    Sentry.setContext(key, context);
  };

  /**
   * Add breadcrumb for user actions
   */
  const addBreadcrumb = (
    message: string,
    category: string = "user",
    level: Sentry.SeverityLevel = "info"
  ) => {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      timestamp: Date.now() / 1000,
    });
  };

  /**
   * Capture an exception with additional context
   */
  const captureException = (error: Error, context?: Record<string, any>) => {
    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }
  };

  /**
   * Capture a message with additional context
   */
  const captureMessage = (
    message: string,
    level: Sentry.SeverityLevel = "info",
    context?: Record<string, any>
  ) => {
    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
        Sentry.captureMessage(message, level);
      });
    } else {
      Sentry.captureMessage(message, level);
    }
  };

  /**
   * Start a performance span
   */
  const startSpan = (name: string, operation: string = "function") => {
    return Sentry.startSpan(
      {
        name,
        op: operation,
      },
      (span) => span
    );
  };

  /**
   * Profile a function execution
   */
  const profileFunction = async <T>(
    name: string,
    fn: () => Promise<T> | T,
    context?: Record<string, any>
  ): Promise<T> => {
    return Sentry.startSpan(
      {
        name,
        op: "function",
      },
      async (span) => {
        // Add context as span attributes
        if (context) {
          Object.entries(context).forEach(([key, value]) => {
            span.setAttribute(key, value);
          });
        }

        try {
          return await fn();
        } catch (error) {
          captureException(error as Error, { functionName: name, ...context });
          throw error;
        }
      }
    );
  };

  return {
    setContext,
    addBreadcrumb,
    captureException,
    captureMessage,
    startSpan,
    profileFunction,
  };
}
